import { pathOr } from 'ramda';
import { useEffect, useState } from 'react';
import { getType } from '../../../../../../sources/type';
import translations from '../../../../../../translations';
import type { CriteriaType, WhereStatement } from '../../../../../../types/generic';
import type { TypeElementResponseAPI } from '../../../../../../types/type';
import type { ContentType } from '../../../../../../types/value';
import { isNullOrEmpty } from '../../../../../../utils/guard';
import Breadcrumb from '../../../../../Breadcrumb';
import FormGroup from '../../../../../generic/FormGroup';
import ModalBody from '../../../../../generic/modal/ModalBody';
import ModalFooter from '../../../../../generic/modal/ModalFooter';
import Loader from '../../../../../loader/Loader';
import { useNotificationDispatch } from '../../../../../notifications/NotificationProvider';
import ValueSelectorModal from '../../../../../values/selector/ValueSelectorModal';
import { useDataAction } from '../../../contextProviders/DataActionProvider';
import { useMapElement } from '../../../contextProviders/MapElementProvider';
import Footer from '../../Footer';

const CRITERIAS = [
    { label: 'Equal To', value: 'EQUAL' },
    { label: 'Not Equal To', value: 'NOT_EQUAL' },
    { label: 'Greater Than', value: 'GREATER_THAN' },
    { label: 'Greater Than Or Equal To', value: 'GREATER_THAN_OR_EQUAL' },
    { label: 'Less Than', value: 'LESS_THAN' },
    { label: 'Less Than Or Equal To', value: 'LESS_THAN_OR_EQUAL' },
    { label: 'Starts With', value: 'STARTS_WITH' },
    { label: 'Ends With', value: 'ENDS_WITH' },
    { label: 'Contains', value: 'CONTAINS' },
    { label: 'Is Empty', value: 'IS_EMPTY' },
];

const FilterCriteria = () => {
    const [hasSubmitted, updateHasSubmitted] = useState(false);
    const [type, setType] = useState<TypeElementResponseAPI | null>(null);

    const { breadcrumbs, container } = useMapElement();
    const dispatchNotification = useNotificationDispatch();

    const {
        onReturnToDataActionFilterScreen,
        dataActionToEdit,
        criteriaToEdit,
        onSetCriteriaTypePropertyId,
        onSetCriteriaType,
        onSetCriteriaValueElementToReference,
        onApplyCriteria,
    } = useDataAction();

    const { dataAction } = dataActionToEdit;
    const { criteria, index } = criteriaToEdit;

    const typeElementId = dataAction?.objectDataRequest?.typeElementId;

    const { columnTypeElementPropertyId, criteriaType } = criteria as WhereStatement;

    // When choosing a value to be applied to the filter
    // we only want to choose from values that are of the same type
    // as the property that we are filtering by
    const typeToFilter =
        columnTypeElementPropertyId && type
            ? type.properties.find((property) => property.id === columnTypeElementPropertyId)
            : null;

    let contentTypeFilter: ContentType | null = null;

    if (typeToFilter) {
        contentTypeFilter = typeToFilter.contentType;
    }

    if (criteriaType === 'IS_EMPTY') {
        contentTypeFilter = 'ContentBoolean';
    }

    const checkRequiredFields = () => ({
        columnTypeElementPropertyId: pathOr('', ['columnTypeElementPropertyId'], criteria),
        criteriaType: pathOr('', ['criteriaType'], criteria),
        valueElementToReferenceId: pathOr('', ['valueElementToReferenceId', 'id'], criteria),
    });

    const onSave = () => {
        updateHasSubmitted(true);
        const isFormValid = Object.values(checkRequiredFields()).every(
            (field) => !isNullOrEmpty(field),
        );

        if (isFormValid && index !== null) {
            onApplyCriteria(index);
        }
    };

    const fetchType = async () => {
        if (typeElementId) {
            try {
                const typeResult = await getType(typeElementId);
                setType(typeResult);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        fetchType();
    }, []);

    const renderBody = () => {
        const { columnTypeElementPropertyId, criteriaType, valueElementToReferenceId } =
            checkRequiredFields();
        return (
            <>
                <div className="margin-bottom">
                    <Breadcrumb trail={breadcrumbs.trail} activeItemId={breadcrumbs.activeItemId} />
                </div>

                <FormGroup
                    label={translations.DATA_ACTION_property_to_filter_by_label}
                    htmlFor="type-properties"
                    isRequired
                    validationMessage={translations.MAP_ELEMENT_property_field_validation_message}
                    isValid={!isNullOrEmpty(columnTypeElementPropertyId)}
                    showValidation={hasSubmitted}
                >
                    <select
                        id="type-properties"
                        value={columnTypeElementPropertyId}
                        onChange={(e) =>
                            onSetCriteriaTypePropertyId(
                                e.target.value,
                                (e.target[e.target.selectedIndex] as HTMLOptionElement).text,
                            )
                        }
                        className="form-control form-control-width"
                    >
                        <option value="">Select a property</option>
                        {type?.properties.map((property) => (
                            <option key={property.id} value={property.id}>
                                {property.developerName}
                            </option>
                        ))}
                    </select>
                </FormGroup>

                <FormGroup
                    label={translations.DATA_ACTION_criteria_filter}
                    htmlFor="criteria-type"
                    isRequired
                    validationMessage={translations.MAP_ELEMENT_criteria_field_validation_message}
                    isValid={!isNullOrEmpty(criteriaType)}
                    showValidation={hasSubmitted}
                >
                    <select
                        id="criteria-type"
                        defaultValue={criteriaType}
                        onChange={(e) => {
                            onSetCriteriaType(
                                e.target.value as CriteriaType,
                                (e.target[e.target.selectedIndex] as HTMLOptionElement).text,
                            );
                        }}
                        className="form-control form-control-width"
                    >
                        <option value="">Select a criteria</option>
                        {CRITERIAS.map((criteria) => (
                            <option key={criteria.value} value={criteria.value}>
                                {criteria.label}
                            </option>
                        ))}
                    </select>
                </FormGroup>
                <FormGroup
                    isRequired
                    label={translations.DATA_ACTION_criteria_value_applied_label}
                    validationMessage={translations.MAP_ELEMENT_value_field_validation_message}
                    isValid={!isNullOrEmpty(valueElementToReferenceId)}
                    showValidation={hasSubmitted}
                >
                    {criteria && (
                        <ValueSelectorModal
                            value={criteria?.valueElementToReferenceId}
                            onChangeAsValueReference={onSetCriteriaValueElementToReference}
                            container={container}
                            contentType={contentTypeFilter}
                            includeSystemValues
                        />
                    )}
                </FormGroup>
            </>
        );
    };

    const renderFooter = () => (
        <Footer
            save={onSave}
            saveButtonText={translations.DATA_ACTION_save_criteria_button_text}
            cancel={onReturnToDataActionFilterScreen}
        />
    );

    if (!type) {
        return <Loader />;
    }

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default FilterCriteria;
